import {
  Box,
  Button,
  Grid,
  GridItem,
  Input,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Alert,
  AlertIcon,
  Tr,
  Icon,
  Flex,
  Tooltip,
  VisuallyHidden,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  approveProcurement,
  rejectProcurement,
} from "./ProcurementApprovalSlice";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import moment from "moment";
import isAllowed from "../../routing/isAllowed";
import { DocumentViewerLink } from "../../Components/common/DocumentViewerLink";
import DOCUMENT_FROM from "../../Common/Constants";
import CustomAlertDialog from "../../Components/CustomAlertDialog";
import { FaEdit } from "react-icons/fa";
import {
  getAllExternalSuppliers,
  getFarmers,
  getProcByProcId,
  updateProcurement,
  getAllHeaps,
} from "../../api";
import { getCompanyDetails } from "../../redux/company/CompanySlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { getAllPrograms } from "../../redux/program/ProgramSlice";
import { getProductClassification } from "../../redux/product/ProductSlice";
import { checkFileTypesAndSize } from "../../Common/utils";
import { FiUpload } from "react-icons/fi";
import { FileIcon, RemoveIcon } from "../../assets/svgs/Icons";
import axios from "axios";

function ProcurementApplicationDetails() {
  const [status, setStatus] = useState("idle");
  const [remarks, setRemarks] = useState([]);
  const { id } = useParams();
  const [procurementFiles, setProcurementFiles] = useState(null);
  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [procurement, setProcurement] = useState({});
  const [isOpenMod, setIsOpenMod] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { userData } = useSelector((state) => state.auth);
  const [editable, setEditable] = useState(false);
  const { companyDetails } = useSelector((state) => state?.company);
  const { allPrograms } = useSelector((state) => state.programs);
  const { productClassification } = useSelector((state) => state.products);
  const [suppliers, setSuppliers] = useState([]);
  const [allHeaps, setAllHeaps] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const token = localStorage.getItem("cu_user");
  const handleEditClick = () => {
    setEditable(!editable);
  };
  const onClose = () => setIsOpen(false);
  const onCloseMod = () => setIsOpenMod(false);
  const navigate = useNavigate();
  const getProcurements = async (id) => {
    setIsDataLoading(true);
    try {
      const response = await getProcByProcId(id);
      setProcurement(response?.data);
      setIsDataLoading(false);
    } catch (error) {
      setIsDataLoading(false);
    }
    setIsDataLoading(false);
  };
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleFetchHeaps = async () => {
    const response = await getAllHeaps();
    setAllHeaps(response?.data);
  };

  const handleInputChange = (e, field, subField) => {
    const { value } = e.target;
    setProcurement((prevProcurement) => {
      let updatedProcurement = { ...prevProcurement };

      if (field === "date") {
        updatedProcurement.date = value;
      } else if (field === "purchaseSlipNumber") {
        updatedProcurement.purchaseSlipNumber = value;
      } else if (field === "transportDetails" && typeof subField === "number") {
        updatedProcurement.transportDetails = [
          ...prevProcurement.transportDetails,
        ];
        updatedProcurement.transportDetails[subField] = value;
      } else if (subField) {
        updatedProcurement[field][subField] = value;
      } else {
        updatedProcurement[field] = value;
      }

      return updatedProcurement;
    });
  };

  const handleUpdateProcurement = async () => {
    const { procurement_supplier, procurement_unit, ...rest } = procurement;
    let procurementFilesRequest;

    // If procurementFiles are available, make the API call to upload them
    if (procurementFiles) {
      const procurementFilesData = new FormData();

      procurementFilesData.append("document", procurementFiles);
      procurementFilesData.append("doc_type", "OTHER");
      procurementFilesData.append("userType", "CLIENT");
      procurementFilesData.append("documentInfo", "OTHER");

      procurementFilesRequest = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_API_HOST}/api/document`,
        data: procurementFilesData,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
    }
    const updatedProcurement = {
      ...rest,
      supplierId: procurement_supplier?._id,
      unitId: procurement_unit?._id,
    };
    if (procurementFilesRequest?.data?.data?._id) {
      updatedProcurement.procurementDocument =
        procurementFilesRequest.data.data._id;
    }
    const response = await updateProcurement(
      procurement?._id,
      updatedProcurement
    );

    if (response?.status === "success") {
      toast.success("Procurement Updated Successfully");
      navigate("/procurement");
    }
  };

  const handleProcurementFileChange = (event) => {
    const file = event.target.files[0];

    const validation = checkFileTypesAndSize(
      file.name,
      file.type,
      file.size,
      10485760
    );

    if (!validation.isValid) {
      setErrorMessage(validation.error);
      toast.error(validation.error);
      return;
    }

    setProcurementFiles(file);
    setErrorMessage("");

    toast.success("File Uploaded Successfully");
  };

  const clearErrorMessage = () => {
    setErrorMessage("");
  };

  const getPrograms = () => {
    try {
      const result = dispatch(getAllPrograms());
      unwrapResult(result);
    } catch (error) {
      console.error(error);
      unwrapResult(error);
    }
  };

  const getClassification = () => {
    try {
      const result = dispatch(getProductClassification());
      unwrapResult(result);
    } catch (error) {
      console.error(error);
      unwrapResult(error);
    }
  };

  const getAllSuppliers = async () => {
    const response = await getAllExternalSuppliers();
    const farmersResponse = await getFarmers();
    const externalSuppliers = response?.data.map((supplier) => ({
      _id: supplier._id,
      name: `${supplier.name} - ${supplier?.type}`,
    }));
    const farmersSuppliers = farmersResponse?.data.map((farmer) => ({
      _id: farmer._id,
      name: `${farmer.firstName} ${farmer.lastName} - Farmer`,
    }));
    const combinedSuppliers = [...externalSuppliers, ...farmersSuppliers];
    setSuppliers(combinedSuppliers);
  };

  const approveProc = (id) => {
    let values = {
      comment: comment,
    };
    setIsDataLoading(true);
    try {
      setStatus("pending");
      dispatch(approveProcurement({ id: id, values }));
      setStatus("success");
      setIsDataLoading(false);
      navigate(-1);
    } catch (error) {
      setStatus("rejected");
      setIsDataLoading(false);
      console.error(error);
    }
  };

  const rejectProc = (id) => {
    let values = {
      comment: comment,
    };
    setIsDataLoading(true);
    try {
      setStatus("pending");
      dispatch(rejectProcurement({ id: id, values }));
      setStatus("success");
      setIsDataLoading(false);
      navigate(-1);
    } catch (error) {
      setStatus("rejected");
      setIsDataLoading(false);
      console.error(error);
    }
  };

  const getCompany = () => {
    try {
      const result = dispatch(getCompanyDetails(userData?.companyId));
      unwrapResult(result);
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    const remarks = procurement?.remarks?.filter((data) => data.comment);
    setRemarks(remarks);
  }, [procurement?.remarks]);

  useEffect(() => {
    getProcurements(id);
    getAllSuppliers();
    getCompany();
    getPrograms();
    getClassification();
    handleFetchHeaps();
  }, [id]);

  return (
    <Box marginBottom={"6rem"}>
      <Box
        display={"flex"}
        alignItems="center"
        cursor={"pointer"}
        onClick={handleGoBack}
      >
        <IoChevronBackCircleSharp color={"#52BBFF"} size={30} />
        &nbsp;
        <Text
          color={"#52BBFF"}
          fontSize={{ base: "sm", md: "sm", lg: "md" }}
          fontWeight={"800"}
          fontFamily={"font.roboto"}
        >
          Go Back
        </Text>
      </Box>
      <Flex
        marginTop="2rem"
        justifyContent="space-between"
        alignItems="center"
        gap="1rem"
      >
        <Text color={"brand.blue"} fontSize="2xl" fontWeight={"800"}>
          Procurement : {procurement?.id}
        </Text>
        {userData &&
          userData?.role &&
          userData?.role?.includes("CLIENT") &&
          procurement?.status === "INCOMING" && (
            <Button bg="#52BBFF" color="white" onClick={handleEditClick}>
              Edit Procurement &nbsp; <FaEdit />
            </Button>
          )}
      </Flex>
      {procurement?.isUpdated ? (
        <Alert mt="1rem" status="success" variant="solid" color="white">
          <AlertIcon />
          This procurement was updated by the client.
        </Alert>
      ) : (
        ""
      )}
      <Box
        marginTop={"1.5rem"}
        bg={"#52BBFF"}
        color={"#fff"}
        padding={"10px 2rem"}
        fontWeight="700"
        marginLeft="-2rem"
        marginRight={"-2.5rem"}
      >
        Ginner Details
      </Box>
      <Box>
        <Grid
          templateColumns="repeat(2, 1fr)"
          marginTop={"2rem"}
          mb={"2rem"}
          padding={"2rem"}
          bg="white"
          borderRadius="15px"
          boxShadow={" rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"}
          gap={editable ? "1rem" : ""}
        >
          <GridItem colSpan={1}>
            <Text
              color={"brand.blue"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              <Box
                as="span"
                borderRight={"3px solid #2272D5"}
                height="40px"
                marginRight={"1rem"}
              ></Box>
              Ginning Unit
            </Text>
            {editable ? (
              <Select
                mt="10px"
                border="2px solid  #8fd0fd !important"
                bg="#fff"
                color="#141D43"
                value={procurement?.procurement_unit?._id}
                onChange={(e) =>
                  handleInputChange(e, "procurement_unit", "_id")
                }
              >
                <option hidden="true">Select Unit</option>
                {companyDetails?.data?.company_units?.map((data, index) => (
                  <option default="true" value={data?._id} key={index}>
                    {data?.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Text mt="1rem">
                {procurement?.procurement_unit?.name
                  ? procurement?.procurement_unit?.name
                  : "N/A"}
              </Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text
              color={"brand.blue"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Ginning Unit Address
            </Text>
            <Text mt="1rem">
              {procurement?.procurement_unit?.address?.raw_address
                ? procurement?.procurement_unit?.address?.raw_address
                : "N/A"}
            </Text>
          </GridItem>
          <GridItem colSpan={1} mt="2rem">
            <Text
              color={"brand.blue"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Ginning Unit Type
            </Text>
            <Text mt="1rem">
              {procurement?.procurement_unit?.unitType
                ? procurement?.procurement_unit?.unitType
                : "N/A"}
            </Text>
          </GridItem>
          <GridItem colSpan={1} mt="2rem">
            <Text
              color={"brand.blue"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Company Name
            </Text>
            <Text mt="1rem">
              {procurement?.procurement_company?.name
                ? procurement?.procurement_company?.name
                : "N/A"}
            </Text>
          </GridItem>
        </Grid>
      </Box>

      <Box
        marginTop={"1.5rem"}
        bg={"#52BBFF"}
        color={"#fff"}
        padding={"10px 2rem"}
        fontWeight="700"
        marginLeft="-2rem"
        marginRight={"-2.5rem"}
      >
        Supplier Details
      </Box>

      <Box
        bg="white"
        borderRadius="15px"
        boxShadow={" rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"}
        p="1rem"
        mt="2rem"
        mb="2rem"
      >
        <Box p="1rem">
          <Grid templateColumns="repeat(3, 1fr)" gap={editable ? "1rem" : ""}>
            <GridItem colSpan={1}>
              <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                <Box
                  as="span"
                  borderRight={"3px solid #2272D5"}
                  height="40px"
                  marginRight={"1rem"}
                ></Box>
                Supplier's Name
              </Text>
              {editable ? (
                <Select
                  mt="10px"
                  border="2px solid  #8fd0fd !important"
                  bg="#fff"
                  color="#141D43"
                  value={procurement?.procurement_supplier?._id}
                  onChange={(e) =>
                    handleInputChange(e, "procurement_supplier", "_id")
                  }
                >
                  <option hidden="true">Select Supplier</option>
                  {suppliers?.map((data, index) => (
                    <option default="true" value={data?._id} key={index}>
                      {data?.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Text mt="1rem">
                  {procurement?.procurement_supplier?.firstName
                    ? `${procurement?.procurement_supplier?.firstName} ${procurement?.procurement_supplier?.lastName}`
                    : procurement?.procurement_supplier?.name}{" "}
                  -{" "}
                  {procurement?.procurement_supplier?.type ||
                    procurement?.supplierType}
                </Text>
              )}
            </GridItem>
            <GridItem>
              <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                Supplier's ID
              </Text>
              <Text mt="1rem">
                {procurement?.procurement_supplier?.idProof || "N/A"}
              </Text>
            </GridItem>
          </Grid>
        </Box>
        <Box p="1rem">
          <Grid templateColumns="repeat(3, 1fr)" gap={2}>
            {procurement?.procurement_supplier?.contactInfo && (
              <>
                <GridItem colSpan={1}>
                  <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                    Contact Number
                  </Text>
                  <Text mt="1rem">
                    {procurement?.procurement_supplier?.contactInfo?.phone
                      ? procurement?.procurement_supplier?.contactInfo?.phone
                      : "N/A"}
                  </Text>
                </GridItem>
                <GridItem colSpan={1}>
                  <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                    Email
                  </Text>
                  <Text mt="1rem">
                    {procurement?.procurement_supplier?.contactInfo?.email
                      ? procurement?.procurement_supplier?.contactInfo?.email
                      : "N/A"}
                  </Text>
                </GridItem>
              </>
            )}
            <GridItem colSpan={1}>
              <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                Pincode
              </Text>
              <Text mt="1rem">
                {procurement?.procurement_supplier?.address?.pinCode
                  ? procurement?.procurement_supplier?.address?.pinCode
                  : "N/A"}
              </Text>
            </GridItem>
          </Grid>
        </Box>

        <Box p="1rem">
          <Grid templateColumns="repeat(3, 1fr)" gap={3} maxW="100%">
            <GridItem colSpan={1}>
              <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                Address
              </Text>
              <Text mt="1rem" overflow={"hidden"} wrap="wrap">
                {procurement?.procurement_supplier?.address?.raw_address
                  ? procurement?.procurement_supplier?.address?.raw_address
                  : "N/A"}
              </Text>
            </GridItem>
            <GridItem colSpan={1}>
              <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                City
              </Text>
              <Text mt="1rem">
                {procurement?.procurement_supplier?.address?.city
                  ? procurement?.procurement_supplier?.address?.city
                  : "N/A"}
              </Text>
            </GridItem>
            <GridItem colSpan={1}>
              <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                State
              </Text>
              <Text mt="1rem">
                {procurement?.procurement_supplier?.address?.state
                  ? procurement?.procurement_supplier?.address?.state
                  : "N/A"}
              </Text>
            </GridItem>
          </Grid>
          {procurement?.procurement_supplier?.supplier_farmers?.length > 0 && (
            <>
              <Text
                color={"brand.blue"}
                fontSize="md"
                fontWeight={"700"}
                mt="1rem"
              >
                <Box
                  as="span"
                  borderRight={"3px solid #2272D5"}
                  height="40px"
                  marginRight={"1rem"}
                ></Box>
                Supplier's Farmer
              </Text>
              {procurement.procurement_supplier.supplier_farmers.map(
                (farmer, idx) => (
                  <Grid
                    templateColumns="repeat(3, 1fr)"
                    gap={3}
                    maxW="100%"
                    key={farmer.id}
                  >
                    {/* First Name */}
                    <GridItem colSpan={1}>
                      <Text
                        color="brand.blue"
                        fontSize="md"
                        fontWeight="700"
                        mt="1rem"
                      >
                        {idx + 1}. &nbsp; Farmer's First Name
                      </Text>
                      <Text mt="1rem">{farmer?.firstName}</Text>
                    </GridItem>

                    {/* Last Name */}
                    <GridItem colSpan={1}>
                      <Text
                        color="brand.blue"
                        fontSize="md"
                        fontWeight="700"
                        mt="1rem"
                      >
                        Farmer's Last Name
                      </Text>
                      <Text mt="1rem">{farmer?.lastName}</Text>
                    </GridItem>

                    {/* Address Block */}
                    <GridItem colSpan={1}>
                      <Text
                        color="brand.blue"
                        fontSize="md"
                        fontWeight="700"
                        mt="1rem"
                      >
                        Farmer's Address
                      </Text>
                      <Box mt="1rem">
                        <Text>
                          <strong>Address:</strong>{" "}
                          {farmer?.address?.raw_address}
                        </Text>
                        <Text>
                          <strong>District:</strong> {farmer?.address?.district}
                        </Text>
                        <Text>
                          <strong>State:</strong> {farmer?.address?.state}
                        </Text>
                        <Text>
                          <strong>Pin Code:</strong> {farmer?.address?.pinCode}
                        </Text>
                      </Box>
                    </GridItem>
                  </Grid>
                )
              )}
            </>
          )}
        </Box>
      </Box>

      {procurement?.procurement_supplier?.farmer_farm_details && (
        <>
          <Box
            marginTop={"1.5rem"}
            bg={"#52BBFF"}
            color={"#fff"}
            padding={"10px 2rem"}
            fontWeight="700"
            marginLeft="-2rem"
            marginRight={"-2.5rem"}
          >
            Farm Details
          </Box>

          <Box
            bg="white"
            borderRadius="15px"
            boxShadow={" rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"}
            p="1rem"
            mt="2rem"
            mb="2rem"
          >
            <Box p="1rem">
              <Grid templateColumns="repeat(4, 1fr)" marginTop={"1rem"}>
                <GridItem colSpan={1}>
                  <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                    <Box
                      as="span"
                      borderRight={"3px solid #2272D5"}
                      height="40px"
                      marginRight={"1rem"}
                    ></Box>
                    Land Size (Acres)
                  </Text>
                </GridItem>
                <GridItem colSpan={1}>
                  <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                    Cultivation Capacity (kg)
                  </Text>
                </GridItem>

                <GridItem colSpan={1}>
                  <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                    Latitude
                  </Text>
                </GridItem>
                <GridItem colSpan={1}>
                  <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                    Longitude
                  </Text>
                </GridItem>
              </Grid>
            </Box>

            {procurement?.procurement_supplier?.farmer_farm_details?.length ===
            0 ? (
              <Box p="1rem">
                <Text
                  color={"brand.blue"}
                  fontSize="md"
                  textAlign={"center"}
                  fontWeight={"800"}
                >
                  No farm details available
                </Text>
              </Box>
            ) : (
              <>
                {procurement?.procurement_supplier?.farmer_farm_details?.map(
                  (el) => {
                    return (
                      <>
                        <Box p="1rem">
                          <Grid
                            templateColumns="repeat(4, 1fr)"
                            marginTop={"1rem"}
                          >
                            <GridItem colSpan={1}>
                              <Text
                                color={"brand.blue"}
                                fontSize="md"
                                fontWeight={"700"}
                              >
                                {el?.landSize ? el?.landSize : "N/A"}
                              </Text>
                            </GridItem>
                            <GridItem colSpan={1}>
                              <Text
                                color={"brand.blue"}
                                fontSize="md"
                                fontWeight={"700"}
                              >
                                {el?.cultivationCapacity
                                  ? el?.cultivationCapacity
                                  : "N/A"}
                              </Text>
                            </GridItem>

                            <GridItem colSpan={1}>
                              <Text
                                color={"brand.blue"}
                                fontSize="md"
                                fontWeight={"700"}
                              >
                                <Text
                                  color={"brand.blue"}
                                  fontSize="md"
                                  fontWeight={"700"}
                                >
                                  {el?.address?.location?.coordinates?.[0]
                                    ? el?.address?.location?.coordinates?.[0]
                                    : "N/A"}
                                </Text>
                              </Text>
                            </GridItem>

                            <GridItem colSpan={1}>
                              <Text
                                color={"brand.blue"}
                                fontSize="md"
                                fontWeight={"700"}
                              >
                                {el?.address?.location?.coordinates?.[1]
                                  ? el?.address?.location?.coordinates?.[1]
                                  : "N/A"}
                              </Text>
                            </GridItem>
                          </Grid>
                        </Box>
                      </>
                    );
                  }
                )}
              </>
            )}
          </Box>
        </>
      )}

      <Box
        marginTop={"2rem"}
        bg={"#52BBFF"}
        color={"#fff"}
        padding={"10px 2rem"}
        fontWeight="700"
        marginLeft="-2rem"
        marginRight={"-2.5rem"}
      >
        Procurement Details
      </Box>
      <Box
        bg="white"
        borderRadius="15px"
        boxShadow={" rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"}
        p="1rem"
        mt="2rem"
        mb="2rem"
      >
        <Grid
          templateColumns="repeat(5, 1fr)"
          marginTop={"1rem"}
          p="1rem"
          gap={editable ? "2rem" : ""}
        >
          <GridItem colSpan={1}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              <Box
                as="span"
                borderRight={"3px solid #2272D5"}
                height="40px"
                marginRight={"1rem"}
              ></Box>
              Program
            </Text>
            {editable ? (
              <Select
                marginTop={"10px"}
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.procurement_program?._id}
                onChange={(e) =>
                  handleInputChange(e, "procurement_program", "_id")
                }
              >
                <option hidden="true">Select Program</option>
                {allPrograms?.data?.map((data, index) => (
                  <option value={data?._id} key={index}>
                    {data?.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Text
                mt="1rem"
                maxWidth="150px"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {procurement?.procurement_program?.name}
              </Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
              Procurement Type
            </Text>
            {/* {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.stockQuantity?.netWeight}
                mt="10px"
                onChange={(e) =>
                  handleInputChange(e, "stockQuantity", "netWeight")
                }
              />
            ) : ( */}
            <Text mt="1rem">{procurement?.type}</Text>
            {/* )} */}
          </GridItem>
          <GridItem colSpan={1}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Product Classification
            </Text>
            {editable ? (
              <Select
                marginTop={"10px"}
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.product_productClassification?._id}
                onChange={(e) =>
                  handleInputChange(e, "product_productClassification", "_id")
                }
              >
                {productClassification?.data?.map((data, index) => (
                  <option value={data?._id} key={index}>
                    {data?.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Text mt="1rem">
                {procurement?.product_productClassification?.name}
              </Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
              Net Weight (Kg)
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.stockQuantity?.netWeight}
                mt="10px"
                onChange={(e) =>
                  handleInputChange(e, "stockQuantity", "netWeight")
                }
              />
            ) : (
              <Text mt="1rem">{procurement?.stockQuantity?.netWeight}</Text>
            )}
          </GridItem>

          <GridItem colSpan={1}>
            <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
              Gross Weight (Kg)
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.stockQuantity?.grossWeight}
                mt="10px"
                onChange={(e) =>
                  handleInputChange(e, "stockQuantity", "grossWeight")
                }
              />
            ) : (
              <Text mt="1rem">{procurement?.stockQuantity?.grossWeight}</Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
              Certified Weight (Kg)
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.stockQuantity?.certifiedWeight}
                mt="10px"
                onChange={(e) =>
                  handleInputChange(e, "stockQuantity", "certifiedWeight")
                }
              />
            ) : (
              <Text mt="1rem">
                {procurement?.stockQuantity?.certifiedWeight || "N/A"}
              </Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
              Lot Number
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.lotNumber}
                mt="10px"
                onChange={(e) => handleInputChange(e, "lotNumber")}
              />
            ) : (
              <Text mt="1rem">{procurement?.lotNumber || "N/A"}</Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
              Trade Name
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.tradeName}
                mt="10px"
                onChange={(e) => handleInputChange(e, "tradeName")}
              />
            ) : (
              <Text mt="1rem">{procurement?.tradeName || "N/A"}</Text>
            )}
          </GridItem>
        </Grid>
        <Grid
          templateColumns="repeat(5, 1fr)"
          marginTop={"1rem"}
          p="1rem"
          gap={editable ? "2rem" : ""}
        >
          {procurement?.procurement_supplier?.type === "EXTERNAL_CLIENT" ? (
            <></>
          ) : (
            <GridItem colSpan={1}>
              <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
                Heap
              </Text>
              {editable ? (
                <Select
                  mt="10px"
                  border="2px solid  #8fd0fd !important"
                  bg="#fff"
                  color="#141D43"
                  value={procurement?.procurement_heap?.number}
                  onChange={(e) =>
                    handleInputChange(e, "procurement_heap", "number")
                  }
                >
                  {allHeaps?.map((data, index) => (
                    <option value={data?.number} key={index}>
                      {data?.number}
                    </option>
                  ))}
                </Select>
              ) : (
                <Text mt="1rem">{procurement?.procurement_heap?.number}</Text>
              )}
            </GridItem>
          )}

          <GridItem colSpan={1}>
            <Text color={"brand.blue"} fontSize="md" fontWeight={"700"}>
              Document
            </Text>

            <Box
              boxShadow={"rgba(100, 100, 111, 0.2) 0px 10px 26px 0px"}
              borderRadius="15px"
              w="200px"
            >
              <DocumentViewerLink
                document={procurement?.procurement_documents}
                referenceId={procurement?._id}
                doc_from={DOCUMENT_FROM.PROCUREMENT}
              />
            </Box>
            <Box mt="1rem">
              {editable ? (
                procurementFiles ? (
                  <Box
                    display={"flex"}
                    className="overflow-style"
                    boxShadow="rgba(149, 157, 165, 0.2) 0px 8px 24px"
                  >
                    <>
                      <Box bg="#FFFFFF" w={"250px"} borderRadius={"15px"}>
                        <Box display={"flex"} justifyContent={"right"}>
                          <Button
                            borderColor="#FFFFFF"
                            bg="#ffffff"
                            size="sm"
                            variant="outline"
                            onClick={() => setProcurementFiles(null)}
                            leftIcon={<RemoveIcon />}
                          ></Button>
                        </Box>
                        <Box display={"flex"} justifyContent={"center"}>
                          <FileIcon />
                        </Box>
                        <Tooltip label={procurementFiles?.name}>
                          <Text
                            margin={"auto"}
                            width={"130px"}
                            whiteSpace={"nowrap"}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                          >
                            {procurementFiles?.name}
                          </Text>
                        </Tooltip>
                      </Box>
                    </>
                  </Box>
                ) : (
                  <Tooltip label="Please exercise caution when uploading a document, as once it has been uploaded, it cannot be removed or changed.">
                    <Button
                      color="blue.400"
                      bg={"#FFFFFF"}
                      leftIcon={<FiUpload />}
                      borderRadius={"15px"}
                      as="label"
                      htmlFor="file-upload"
                      variant="outline"
                    >
                      Upload
                      <VisuallyHidden>
                        <input
                          id="file-upload"
                          type="file"
                          onChange={handleProcurementFileChange}
                        />
                      </VisuallyHidden>
                    </Button>
                  </Tooltip>
                )
              ) : (
                <></>
              )}
            </Box>
            <Box mt="1rem" display={"flex"} justifyContent={"center"}>
              {errorMessage && (
                <Box
                  bg="crimson"
                  color="white"
                  p="1rem"
                  borderRadius="15px"
                  mb="1rem"
                  w="60%"
                  textAlign={"center"}
                >
                  {errorMessage}
                  <Button
                    size="sm"
                    variant="outline"
                    ml="1rem"
                    bg="white"
                    color="black"
                    onClick={clearErrorMessage}
                  >
                    <RemoveIcon />
                  </Button>
                </Box>
              )}
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Vehicle Number
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.transportDetails[0]}
                mt="10px"
                onChange={(e) => handleInputChange(e, "transportDetails", 0)}
              />
            ) : (
              <Text
                mt="1rem"
                maxWidth="150px"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {procurement?.transportDetails
                  ? procurement?.transportDetails[0]
                  : "N/A"}
              </Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Invoice Number
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={procurement?.purchaseSlipNumber}
                mt="10px"
                onChange={(e) => handleInputChange(e, "purchaseSlipNumber")}
              />
            ) : (
              <Text
                mt="1rem"
                maxWidth="150px"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {procurement?.purchaseSlipNumber || "N/A"}
              </Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Procurement Date
            </Text>
            {editable ? (
              <Input
                type="date"
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={
                  procurement?.date
                    ? new Date(procurement.date).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => handleInputChange(e, "date")}
                mt="10px"
              />
            ) : (
              <Text
                mt="1rem"
                maxWidth="150px"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {moment(procurement?.date).format("DD-MM-YYYY") || "N/A"}
              </Text>
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Composition
            </Text>
            {editable ? (
              <Input
                bgColor="white"
                color={"#464C64"}
                border="2px solid  #8fd0fd !important"
                fontSize="sm"
                value={
                  procurement?.description ? procurement?.description : "N/A"
                }
                onChange={(e) => handleInputChange(e, "description")}
                mt="10px"
              />
            ) : (
              <Text
                mt="1rem"
                maxWidth="150px"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {procurement?.description || "N/A"}
              </Text>
            )}
          </GridItem>
        </Grid>
      </Box>

      {/* {!userData?.role?.includes("GINNER") && ( */}
      <Grid
        templateColumns="repeat(1, 1fr)"
        style={{ paddingLeft: "0 !important" }}
        width={"100%"}
      >
        <Text
          color={"#141D43"}
          fontSize="md"
          fontWeight={"700"}
          fontFamily={"font.roboto"}
          margin={"1rem"}
        >
          Remarks
        </Text>
        <GridItem
          width={"100%"}
          overflowY={"scroll"}
          className="overflow-style"
        >
          <TableContainer className="overflow-style">
            <Table
              variant={"simple"}
              size="md"
              bg={"#fff"}
              borderRadius={"15px"}
              pr={"1rem"}
            >
              <Thead>
                <Tr>
                  <Th color="brand.blue" textTransform="capitalize">
                    Sr no.
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    Date Time
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    Remarks
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {remarks?.length > 0 ? (
                  remarks.map((remark, index) => {
                    return (
                      <Tr>
                        <Td>{index + 1}</Td>
                        <Td>{`${moment(remark?.time).format(
                          "DD-MM-YYYY hh:mm"
                        )}`}</Td>
                        <Td>{remark?.comment ? remark?.comment : "N/A"}</Td>
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan="4" textAlign="center" fontWeight={"700"}>
                      No Comments Yet
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </GridItem>
      </Grid>

      {!userData?.role?.includes("GINNER") &&
        procurement?.status === "INCOMING" && (
          <>
            <Box>
              <Grid mt={"2rem"} templateColumns="repeat(4, 1fr)">
                <GridItem colSpan={1}>
                  <Textarea
                    type="tel"
                    color={"#141D43"}
                    border="none"
                    width={"522px"}
                    height={"164px"}
                    bg={"#fff"}
                    placeholder="Comment"
                    fontSize={"16px"}
                    borderRadius={"15px"}
                    fontWeight={"400"}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </GridItem>
              </Grid>
            </Box>
            <Box>
              <Grid templateColumns="repeat(6, 1fr)" marginTop={"3rem"}>
                <GridItem colSpan={1}>
                  <Button
                    w={"193px"}
                    h={"50px"}
                    color="brand.white"
                    bg="brand.secondaryColor"
                    colorScheme="brand.secondaryColor"
                    borderRadius={"6px"}
                    shadow="none"
                    disabled={isDataLoading ? true : false}
                    onClick={() => setIsOpenMod(true)}
                  >
                    Accept
                  </Button>
                </GridItem>
                <GridItem colSpan={1}>
                  <Button
                    w={"193px"}
                    h={"50px"}
                    color="brand.white"
                    bg="crimson"
                    colorScheme="brand.secondaryColor"
                    borderRadius={"6px"}
                    shadow="none"
                    disabled={isDataLoading ? true : false}
                    onClick={() => setIsOpen(true)}
                  >
                    Reject
                  </Button>
                </GridItem>
              </Grid>
            </Box>
          </>
        )}

      {editable && (
        <Button
          w={"193px"}
          h={"50px"}
          color="brand.white"
          bg="brand.secondaryColor"
          colorScheme="brand.secondaryColor"
          borderRadius={"6px"}
          shadow="none"
          disabled={isDataLoading ? true : false}
          onClick={() => handleUpdateProcurement()}
        >
          Update
        </Button>
      )}

      <CustomAlertDialog
        isOpen={isOpen}
        onClose={onClose}
        title="Are you sure?"
        content="This action cannot be undone. Do you want to continue?"
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        cancelButtonColor="gray"
        confirmButtonColor="red"
        onConfirm={() => rejectProc(id)}
      />

      <CustomAlertDialog
        isOpen={isOpenMod}
        onClose={onCloseMod}
        title="Are you sure?"
        content="This action cannot be undone. Do you want to continue?"
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        cancelButtonColor="gray"
        confirmButtonColor="#52BBFF"
        onConfirm={() => approveProc(id)}
      />
    </Box>
  );
}

export default isAllowed(ProcurementApplicationDetails);
